/**
 * ProofReflectionForm — pre-upload self-check.
 *
 * Operator brief locked the four fields:
 *   1. Confidence (1–5)
 *   2. What felt difficult (free text)
 *   3. What you struggled with (free text)
 *   4. Self-assessed performance (good / ok / struggled)
 *
 * Submitting persists a reflection row and returns an id which the
 * Training Mode page stitches onto the proof_context the clip basket
 * is created with. The AI later reads the reflection to acknowledge
 * the learner's self-view in coaching feedback.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";
import { MobileDialog } from "../mobile";

const API = process.env.REACT_APP_BACKEND_URL;


export default function ProofReflectionForm({
  competencyId, competencyLabel, onCancel, onSaved,
}) {
  const [confidence, setConfidence] = useState(3);
  const [feltDifficult, setFeltDifficult] = useState("");
  const [struggledWith, setStruggledWith] = useState("");
  const [selfAssessed, setSelfAssessed] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/training/proof/reflection`, {
        competency_id: competencyId,
        confidence,
        felt_difficult: feltDifficult.trim() || null,
        struggled_with: struggledWith.trim() || null,
        self_assessed_performance: selfAssessed || null,
      }, { withCredentials: true });
      onSaved?.(r.data?.reflection_id);
    } catch (_e) {
      toast.error("Couldn't save your reflection. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MobileDialog
      bareContent
      open
      onClose={onCancel}
      testid="proof-reflection-form"
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
          <div>
            <div className="text-xs font-bold tracking-[0.15em] text-slate-500">
              BEFORE YOU UPLOAD
            </div>
            <div className="text-lg font-semibold text-slate-900">
              Quick self-check — {competencyLabel}
            </div>
          </div>
          <button onClick={onCancel} className="p-2 rounded-full hover:bg-slate-100"
                  data-testid="proof-reflection-cancel">
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-5 overflow-y-auto" style={{ maxHeight: "70dvh" }}>
          <Field label="How confident did you feel during this drive?">
            <div className="flex items-center gap-2"
                 data-testid="proof-reflection-confidence">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => setConfidence(n)}
                  className={`flex-1 py-3 rounded-xl text-sm font-semibold transition ${
                    confidence === n
                      ? "bg-emerald-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`proof-reflection-confidence-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-[11px] text-slate-500 mt-1 px-1">
              <span>Shaky</span><span>Very confident</span>
            </div>
          </Field>

          <Field label="What felt difficult?">
            <textarea
              value={feltDifficult}
              onChange={(e) => setFeltDifficult(e.target.value.slice(0, 400))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g. timing the exit at the big roundabout"
              rows={2}
              data-testid="proof-reflection-felt-difficult"
            />
          </Field>

          <Field label="What did you struggle with most?">
            <textarea
              value={struggledWith}
              onChange={(e) => setStruggledWith(e.target.value.slice(0, 400))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g. choosing the right lane when signs were unclear"
              rows={2}
              data-testid="proof-reflection-struggled-with"
            />
          </Field>

          <Field label="How well do you think you performed?">
            <div className="grid grid-cols-3 gap-2"
                 data-testid="proof-reflection-self-assessed">
              {[
                { id: "good",      label: "Good" },
                { id: "ok",        label: "OK" },
                { id: "struggled", label: "Struggled" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setSelfAssessed(opt.id)}
                  className={`py-3 rounded-xl text-sm font-semibold transition ${
                    selfAssessed === opt.id
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`proof-reflection-self-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Field>

          <div className="text-xs text-slate-500 leading-relaxed bg-slate-50 rounded-xl p-3">
            The AI uses your self-check to acknowledge how you felt — it
            doesn't punish you for things you don't yet own.
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={submit}
            disabled={busy}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl disabled:opacity-50 transition active:scale-[0.98]"
            data-testid="proof-reflection-submit"
          >
            {busy ? "Saving…" : "Continue to upload"}
          </button>
        </div>
      </div>
    </MobileDialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-800 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
